import React, { useState, useLayoutEffect, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface SpotlightByIdProps {
  targetId: string;
  open: boolean;
  padding?: number;
  radius?: number;
  backdropColor?: string;
  onClose?: () => void;
}

export function SpotlightById({
  targetId,
  open,
  padding = 4,
  radius = 8,
  backdropColor = "rgba(0,0,0,0.25)",
  onClose,
}: SpotlightByIdProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const lastIdRef = useRef<string>(targetId);

  const targetEl =
    (typeof document !== "undefined" && document.getElementById(targetId)) ||
    null;

  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }

    let frame: number;
    let ro: ResizeObserver;
    let cleanupFn: () => void;

    const waitForElement = () => {
      const el = document.getElementById(targetId);
      if (!el) {
        frame = requestAnimationFrame(waitForElement);
        return;
      }

      const measure = () => setRect(el.getBoundingClientRect());
      frame = requestAnimationFrame(measure);

      ro = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(measure);
      });
      ro.observe(el);

      const onUpdate = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(measure);
      };
      window.addEventListener("scroll", onUpdate, true);
      window.addEventListener("resize", onUpdate);

      const cleanup = () => {
        ro.disconnect();
        window.removeEventListener("scroll", onUpdate, true);
        window.removeEventListener("resize", onUpdate);
        cancelAnimationFrame(frame);
      };

      cleanupFn = cleanup;
    };

    waitForElement();

    return () => {
      cancelAnimationFrame(frame);
      cleanupFn?.();
    };
  }, [open, targetId]);

  useEffect(() => {
    if (open && targetEl && lastIdRef.current !== targetId) {
      targetEl.scrollIntoView({ block: "center", behavior: "smooth" });
      lastIdRef.current = targetId;
    }
  }, [open, targetId, targetEl]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <button
        type="button"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999,
        }}
        onClick={(e) => {
          onClose?.();
        }}
      />
      {rect && (
        <svg
          width="100vw"
          height="100vh"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999,
            pointerEvents: "none",
            transition: "all 200ms",
          }}
        >
          <defs>
            <filter
              id="spotlight-blur"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
              filterUnits="objectBoundingBox"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
            </filter>

            <mask
              id="spotlight-mask-single"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="100%"
              height="100%"
            >
              {/* white = opaque, black = transparent */}
              <rect width="100%" height="100%" fill="white" />

              {/* apply the blur filter to the “hole” */}
              <rect
                x={rect.left - padding}
                y={rect.top - padding}
                width={rect.width + padding * 2}
                height={rect.height + padding * 2}
                rx={radius}
                ry={radius}
                fill="black"
                filter="url(#spotlight-blur)"
              />
            </mask>
          </defs>

          <rect
            width="100%"
            height="100%"
            fill={backdropColor}
            mask="url(#spotlight-mask-single)"
          />
        </svg>
      )}
    </>,
    document.body
  );
}
